import React, { useCallback } from 'react';
import { getIn } from 'formik';

import { FieldElementPropTypes } from '../propTypes';
import { TextField } from 'core/components/text-field';
import { Autocomplete } from 'core/components/autocomplete';

const AutocompleteField = ({
  field: {
    id,
    name,
    label,
    mode,
    options,
    value,
    optional,
    onSelection,
    allowUserInput,
    optionsLimit,
    helperText,
    ...props
  },
  ctx: { setFieldValue, touched, errors, classes, setFieldTouched }
}) => {
  const handleSelectChange = useCallback(
    (_event, val) => {
      setFieldTouched(id, true, false);
      setFieldValue(id, val ? val.id : '');
      if (typeof onSelection === 'function') {
        onSelection(id, val);
      }
    },
    [onSelection, setFieldValue, setFieldTouched, id]
  );

  const handleInputChange = useCallback(
    (event) => {
      const {
        target: { value }
      } = event;
      setFieldTouched(id || name, true, false);
      setFieldValue(id || name, value);
      if (typeof onSelection === 'function') {
        onSelection(id || name, value);
      }
    },
    [onSelection, setFieldValue, setFieldTouched, name, id]
  );

  let filteredOptions = value
    ? options.filter((option) => {
        const text = option.text || option.value;
        return (
          text?.toLowerCase()?.includes(value?.toLowerCase()) ||
          option?.id?.toString().toLowerCase().includes(value?.toLowerCase())
        );
      })
    : options;

  // If optionsLimit provided, limit options
  if (optionsLimit) {
    filteredOptions = filteredOptions.slice(0, optionsLimit);
  }

  // If no options found for the input value, display a custom option if allowUserInput is true
  const updatedOptions =
    !filteredOptions.length && allowUserInput && value ? [{ id: value, text: value, custom: true }] : filteredOptions;

  const currentValue =
    updatedOptions.find((option) => {
      // This helps correctly work with state values during address autocomplete
      const optionId = allowUserInput ? option.id?.toString().toLowerCase() : option.id?.toString();
      const valueToMatch = allowUserInput ? value?.toLowerCase() : value;

      return optionId === valueToMatch || option.text?.toLowerCase() === valueToMatch;
    }) || (allowUserInput ? { id: value, text: value } : null);

  return (
    <Autocomplete
      value={currentValue}
      classes={classes}
      name={name}
      id={id}
      options={updatedOptions}
      onSelection={handleSelectChange}
      renderOption={(option) => {
        return option.custom ? `custom: ${option.text}` : option.text;
      }}
      errors={errors}
      touched={touched}
      InputComponent={TextField}
      inputProps={{
        label,
        mode,
        onChange: handleInputChange,
        error: getIn(touched, name || id) && !!getIn(errors, name || id),
        helperText: helperText ? helperText : getIn(touched, name || id) ? getIn(errors, name || id) : undefined,
        disabled: props.disabled
      }}
      {...props}
    />
  );
};

AutocompleteField.propTypes = FieldElementPropTypes;

export default AutocompleteField;
